import { DBCollection } from './constants-shared';
import { readFromCache, saveToCache, deleteFromCache } from './cache-utils';
import { appId } from '../constants';
import { doc, getDoc, getFirestore } from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { startTrace, stopTrace } from '../analytics';

/**
 * Three-tier lazy data loader: Memory → Cache → Firestore
 * 
 * This class manages on-demand loading of individual documents with caching.
 * It's designed for large collections that shouldn't be loaded upfront.
 */
export class LazyDataLoader<T extends { id?: string; passageId?: string }> {
  private memoryCache: Map<string, T> = new Map();
  private loadingPromises: Map<string, Promise<T | undefined>> = new Map();
  private collection: DBCollection;
  private idField: keyof T;

  constructor(collection: DBCollection, idField: keyof T = 'passageId' as keyof T) {
    this.collection = collection;
    this.idField = idField;
  }

  /**
   * Get an item by ID using three-tier loading:
   * 1. Check memory cache (instant)
   * 2. Check browser cache (fast)
   * 3. Fetch from Firestore (fallback)
   */
  async get(id: string): Promise<T | undefined> {
    // Tier 1: Memory cache
    if (this.memoryCache.has(id)) {
      // console.log(`  ⚡ ${this.collection}[${id}] from memory`);
      return this.memoryCache.get(id);
    }

    // Prevent duplicate concurrent loads
    if (this.loadingPromises.has(id)) {
      return this.loadingPromises.get(id);
    }

    // Start loading
    const loadPromise = this.loadItem(id);
    this.loadingPromises.set(id, loadPromise);

    try {
      const item = await loadPromise;
      return item;
    } finally {
      this.loadingPromises.delete(id);
    }
  }

  private async loadItem(id: string): Promise<T | undefined> {
    const loaderTrace = startTrace(`lazy_load_${this.collection}`);
    let source: 'cache' | 'firestore' = 'cache';

    // Tier 2: Browser cache
    try {
      const cacheKey = `${this.collection}/${id}`;
      const cached = await readFromCache<T>(appId, cacheKey);

      if (cached) {
        this.memoryCache.set(id, cached);
        stopTrace(loaderTrace, { id, source: 'cache' });
        return cached;
      }
    } catch (error) {
      console.warn(`Cache read failed for ${this.collection}[${id}]:`, error);
    }

    // Tier 3: Firestore
    try {
      source = 'firestore';
      const db = getFirestore(getApp());
      const docRef = doc(db, this.collection, id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = {
          id: docSnap.id,
          ...docSnap.data()
        } as T;

        // Save to caches
        this.memoryCache.set(id, data);
        const cacheKey = `${this.collection}/${id}`;
        saveToCache(appId, cacheKey, data).catch(err =>
          console.warn(`Failed to cache ${this.collection}[${id}]:`, err)
        );

        stopTrace(loaderTrace, { id, source: 'firestore' });
        return data;
      }

      stopTrace(loaderTrace, { id, status: 'not_found' });
      return undefined;
    } catch (error) {
      stopTrace(loaderTrace, { id, status: 'error' });
      console.error(`Failed to load ${this.collection}[${id}] from Firestore:`, error);
      return undefined;
    }
  }

  /**
   * Preload items into memory cache (e.g., from initial hydration)
   */
  preload(items: T[]): void {
    items.forEach(item => {
      const id = item[this.idField] as string;
      if (id) {
        this.memoryCache.set(id, item);
      }
    });
  }

  /**
   * Get all items currently in memory cache
   */
  getAllCached(): T[] {
    return Array.from(this.memoryCache.values());
  }

  /**
   * Clear memory cache
   */
  clear(): void {
    this.memoryCache.clear();
    this.loadingPromises.clear();
  }

  /**
   * Invalidate a specific item from all caches (memory + browser)
   * Forces the next get() to fetch fresh data from Firestore
   */
  async invalidate(id: string): Promise<void> {
    // Clear from memory cache
    this.memoryCache.delete(id);

    // Clear from browser cache
    try {
      const cacheKey = `${this.collection}/${id}`;
      await deleteFromCache(appId, cacheKey);
    } catch (error) {
      console.warn(`Failed to invalidate cache for ${this.collection}[${id}]:`, error);
    }
  }
}

